/**
 * Governance Drift Detection Page (F038)
 * Route: /governance-drift | Nav: Model > Governance Drift
 *
 * Control-plane view over drift signals raised by REGE's detectors and the per-identity
 * governance-drift state they reduce to. Built on the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and `-behaviours.context.md`, mirroring
 * `src/pages/McpGovernancePage.tsx` (F032) for conventions.
 *
 * Binds to `GovernanceDriftController` (`api/portal/governance-drift`) via `governanceDriftApi`.
 *
 * Only `Identity` and `Autonomous` categories have wired detectors in this MVP — `Connector`,
 * `Operational`, and `Governance` will show empty facets in the signal list until their
 * detectors land. That is expected, not a bug.
 *
 * NB: there is no single-signal-by-id endpoint on `GovernanceDriftController` — the detail
 * drawer renders straight from the already-loaded row object (every `DriftSignal` field,
 * including `resolution`/`resolvedAt`, is present in the list payload) rather than issuing a
 * second `['drift-signal-detail', id]` fetch.
 */
import { useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Text,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Dropdown,
  Option,
  Spinner,
  Toaster,
  Toast,
  ToastTitle,
  ToastBody,
  useId,
  useToastController,
  makeStyles,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Field,
  Textarea,
} from '@fluentui/react-components';
import type { BadgeProps } from '@fluentui/react-components';
import {
  ChevronRightRegular,
  ArrowClockwise24Regular,
  Bookmark24Regular,
  PulseRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { governanceDriftApi } from '../services/api';
import type {
  DriftCategory,
  DriftIdentityState,
  DriftSeverity,
  DriftSignal,
  DriftState,
  DriftStatus,
} from '../types';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';
import {
  DetailDrawer,
  DetailSection,
  DetailField,
  DetailMetadataGrid,
} from '../components/common/DetailDrawer';

// ---------------------------------------------------------------------------
// Enum option lists (verbatim C# enum member order)
// ---------------------------------------------------------------------------

const DRIFT_CATEGORY_OPTIONS: DriftCategory[] = ['Identity', 'Autonomous', 'Connector', 'Operational', 'Governance'];
const DRIFT_STATUS_OPTIONS: DriftStatus[] = ['Open', 'Acknowledged', 'AutoResolved', 'Resolved', 'AutoResponded'];

/** Categories with a live detector today. The other three exist in the enum but have no detector
 *  wired yet — filtering to one of them, or reading a "None" identity state, reflects "not yet
 *  measured" for that dimension, never a confirmed absence of drift. Surfaced in copy below rather
 *  than silently rendered as a clean result. */
const MEASURED_DRIFT_CATEGORIES: DriftCategory[] = ['Identity', 'Autonomous'];

// ---------------------------------------------------------------------------
// Badge color mapping — semantic maps, not raw hex (per List & Grid Standard §15).
// ---------------------------------------------------------------------------

const severityColor = (severity: DriftSeverity): BadgeProps['color'] => {
  switch (severity) {
    case 'Critical': return 'danger';
    case 'High': return 'severe';
    case 'Medium': return 'warning';
    case 'Low': return 'informative';
    case 'Negligible':
    default: return 'subtle';
  }
};

/** Lower rank = higher severity — used for the Score/Severity column sort comparator, matching
 *  the C# `DriftSeverity` enum's declared order (mirrors `RISK_TIER_RANK` in McpGovernancePage). */
const DRIFT_SEVERITY_RANK: Record<DriftSeverity, number> = {
  Negligible: 0,
  Low: 1,
  Medium: 2,
  High: 3,
  Critical: 4,
};

const statusColor = (status: DriftStatus): BadgeProps['color'] => {
  switch (status) {
    case 'Open': return 'warning';
    case 'Acknowledged': return 'informative';
    case 'AutoResolved': return 'success';
    case 'Resolved': return 'success';
    case 'AutoResponded': return 'brand';
    default: return 'subtle';
  }
};

/** Terminal statuses carry a `resolvedAt`/`resolution` — the detail drawer only shows those
 *  fields when the signal has actually resolved. */
const TERMINAL_STATUSES: DriftStatus[] = ['AutoResolved', 'Resolved', 'AutoResponded'];

const driftStateColor = (state: DriftState): BadgeProps['color'] => {
  switch (state) {
    case 'None': return 'success';
    case 'Minor': return 'informative';
    case 'Moderate': return 'warning';
    case 'Severe': return 'danger';
    default: return 'subtle';
  }
};

function getDriftSignalSortValue(item: DriftSignal, field: string): unknown {
  if (field === 'severity') return DRIFT_SEVERITY_RANK[item.severity];
  return (item as unknown as Record<string, unknown>)[field];
}

// ---------------------------------------------------------------------------
// Filter state — 2 server-side facets, encoded into the shared `filters` URL param (§17/§19),
// `field:op:value` pairs joined by ';' — same convention as McpGovernancePage / AgentRegistryPage.
// ---------------------------------------------------------------------------

interface DriftFilterState {
  category: DriftCategory | '';
  status: DriftStatus | '';
}

const EMPTY_DRIFT_FILTERS: DriftFilterState = { category: '', status: '' };

function parseDriftFilters(raw: string): DriftFilterState {
  const state: DriftFilterState = { ...EMPTY_DRIFT_FILTERS };
  if (!raw) return state;
  for (const part of raw.split(';')) {
    const [field, , ...rest] = part.split(':');
    const value = rest.join(':');
    if (!field || !value) continue;
    switch (field) {
      case 'category': state.category = value as DriftCategory; break;
      case 'status': state.status = value as DriftStatus; break;
      default: break;
    }
  }
  return state;
}

function serializeDriftFilters(state: DriftFilterState): string {
  const parts: string[] = [];
  if (state.category) parts.push(`category:eq:${state.category}`);
  if (state.status) parts.push(`status:eq:${state.status}`);
  return parts.join(';');
}

function toDriftSignalFilter(state: DriftFilterState): { category?: DriftCategory; status?: DriftStatus } {
  return {
    category: state.category || undefined,
    status: state.status || undefined,
  };
}

/**
 * Server-side skip/take pagination is assumed clamped by `GovernanceDriftController` at parity
 * with the other control-plane list endpoints (1-200) — set to match the adaptive grid's
 * client/server split invariant (never filter/sort/search a partial set — see
 * `.github/context/list-grid-standard-behaviours.context.md` §10).
 */
const DRIFT_SIGNAL_GRID_THRESHOLD = 200;

async function fetchSignalsPage(
  filter: { category?: DriftCategory; status?: DriftStatus },
  params: ListQueryParams
): Promise<PagedResult<DriftSignal>> {
  const take = params.pageSize;
  const skip = (params.page - 1) * take;
  const result = await governanceDriftApi.getSignals(filter, skip, take);
  const totalPages = Math.max(Math.ceil(result.totalCount / take), 1);
  return {
    items: result.items,
    totalCount: result.totalCount,
    pageNumber: params.page,
    pageSize: take,
    totalPages,
    hasPreviousPage: params.page > 1,
    hasNextPage: params.page < totalPages,
  };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  filterDropdown: {
    minWidth: '9rem',
  },
  headerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  identityCard: {
    flexShrink: 0,
  },
  identityBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
  },
  identityLookupRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  identityLookupInput: {
    minWidth: '20rem',
  },
  identityResult: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  identityResultHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  identityEmpty: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
  subList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  signalRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  signalRowMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  signalRowBadges: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
  },
});

const crumbs: Crumb[] = [{ label: 'Governance Drift' }];

// ---------------------------------------------------------------------------
// SignalCompactRow — shared compact rendering for a `DriftSignal`, used by the identity-state
// panel's `openSignals` list (mirrors `UnapprovedUsagePanel`'s row shape in McpGovernancePage).
// ---------------------------------------------------------------------------

const SignalCompactRow: FC<{ signal: DriftSignal; styles: ReturnType<typeof useStyles> }> = ({ signal, styles }) => (
  <div className={styles.signalRow}>
    <div className={styles.signalRowMain}>
      <Text weight="semibold">{signal.targetName}</Text>
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
        {signal.signalKind} · {signal.category} · Score {signal.score.toFixed(1)}
      </Caption1>
    </div>
    <div className={styles.signalRowBadges}>
      <Badge appearance="tint" color={severityColor(signal.severity)}>{signal.severity}</Badge>
      <Badge appearance="tint" color={statusColor(signal.status)}>{signal.status}</Badge>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// IdentityStatePanel — on-demand identity-state lookup (a section above the list frame, mirrors
// `UnapprovedUsagePanel`'s card placement). `getIdentityState` never 404s for "no signals" — it
// always returns 200 with `state: 'None', openSignals: []` — so the only states this needs are
// idle / loading / error / result, never a 404-shaped empty state.
// ---------------------------------------------------------------------------

const IdentityStatePanel: FC = () => {
  const styles = useStyles();
  const [externalIdInput, setExternalIdInput] = useState('');
  const [lookupExternalId, setLookupExternalId] = useState<string | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useQuery<DriftIdentityState>({
    queryKey: ['drift-identity-state', lookupExternalId],
    queryFn: () => governanceDriftApi.getIdentityState(lookupExternalId!),
    enabled: lookupExternalId !== null && lookupExternalId !== '',
  });

  const handleCheckState = () => {
    const trimmed = externalIdInput.trim();
    if (!trimmed) return;
    if (trimmed === lookupExternalId) {
      refetch();
    } else {
      setLookupExternalId(trimmed);
    }
  };

  let resultBody: ReactNode = null;
  if (lookupExternalId) {
    if (isLoading || isFetching) {
      resultBody = (
        <div className={styles.identityEmpty}>
          <Spinner size="tiny" />
          <Body2>Checking drift state…</Body2>
        </div>
      );
    } else if (isError) {
      resultBody = (
        <ErrorState message="Unable to load this identity's drift state." onRetry={() => refetch()} />
      );
    } else if (data) {
      resultBody = (
        <div className={styles.identityResult}>
          <div className={styles.identityResultHeader}>
            <Text weight="semibold">{data.targetExternalId}</Text>
            <Badge appearance="tint" color={driftStateColor(data.state)}>{data.state}</Badge>
          </div>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Reflects Identity and Autonomous signals only — Connector, Operational, and Governance
            detectors are not yet available, so this state does not confirm drift is absent in
            those dimensions.
          </Caption1>
          {data.openSignals.length === 0 ? (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>No open drift signals for this identity.</Caption1>
          ) : (
            <div className={styles.subList}>
              {data.openSignals.map((signal) => (
                <SignalCompactRow key={signal.id} signal={signal} styles={styles} />
              ))}
            </div>
          )}
        </div>
      );
    }
  }

  return (
    <Card className={styles.identityCard}>
      <CardHeader
        image={<SearchRegular />}
        header={<Text weight="semibold">Identity Drift State</Text>}
        description={<Caption1>Look up the reduced governance-drift state for a single identity by external ID</Caption1>}
      />
      <div className={styles.identityBody}>
        <div className={styles.identityLookupRow}>
          <Input
            className={styles.identityLookupInput}
            placeholder="Identity external ID…"
            value={externalIdInput}
            onChange={(_, data) => setExternalIdInput(data.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCheckState();
            }}
          />
          <Button appearance="secondary" onClick={handleCheckState} disabled={!externalIdInput.trim()}>
            Check State
          </Button>
        </div>
        {resultBody}
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// GovernanceDriftPage
// ---------------------------------------------------------------------------

export const GovernanceDriftPage: FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const toasterId = useId('governance-drift-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const [selectedSignal, setSelectedSignal] = useState<DriftSignal | null>(null);

  // Rebaseline dialog — optional reason capture.
  const [isRebaselineDialogOpen, setIsRebaselineDialogOpen] = useState(false);
  const [rebaselineReason, setRebaselineReason] = useState('');

  const closeRebaselineDialog = () => {
    setIsRebaselineDialogOpen(false);
    setRebaselineReason('');
  };

  const recomputeMutation = useMutation({
    mutationFn: () => governanceDriftApi.recompute(),
    onSuccess: (summary) => {
      queryClient.invalidateQueries({ queryKey: ['drift-signals'] });
      dispatchToast(
        <Toast>
          <ToastTitle>Drift Recomputed</ToastTitle>
          <ToastBody>{summary.opened} opened · {summary.resolved} resolved</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 5000 }
      );
    },
  });

  const rebaselineMutation = useMutation({
    mutationFn: (reason?: string) => governanceDriftApi.rebaseline(reason),
    onSuccess: (baseline) => {
      queryClient.invalidateQueries({ queryKey: ['drift-signals'] });
      dispatchToast(
        <Toast>
          <ToastTitle>Baseline Cut</ToastTitle>
          <ToastBody>Version {baseline.version} · {baseline.metricCount} metrics</ToastBody>
        </Toast>,
        { intent: 'success', timeout: 5000 }
      );
      closeRebaselineDialog();
    },
  });

  const handleRebaselineSubmit = () => {
    const trimmed = rebaselineReason.trim();
    rebaselineMutation.mutate(trimmed || undefined);
  };

  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({ defaultSort: 'detectedAt:desc' });

  const filterState = parseDriftFilters(state.filters);
  const driftSignalFilter = toDriftSignalFilter(filterState);

  const updateFilter = <K extends keyof DriftFilterState>(key: K, value: DriftFilterState[K]) =>
    setFilters(serializeDriftFilters({ ...filterState, [key]: value }));

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const grid = useAdaptiveGridData<DriftSignal>({
    // The filters string is folded INTO queryKeyBase (not passed as `filtersSerialized`) because
    // these facets are applied SERVER-SIDE by `GovernanceDriftController` — a filter change must
    // produce a fresh probe/page fetch, not an in-memory re-filter of an already-filtered set.
    queryKeyBase: ['drift-signals', state.filters],
    fetchPage: (params) => fetchSignalsPage(driftSignalFilter, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['targetName', 'targetExternalId', 'signalKind'],
    sortState,
    getValue: getDriftSignalSortValue,
    threshold: DRIFT_SIGNAL_GRID_THRESHOLD,
  });

  const {
    items: signals,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  const actionButtonCount = 1; // chevron detail only
  const columnCount = 7; // targetName, category, signalKind, score, severity, status, detectedAt

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load drift signals." onRetry={refetch} />;
  } else if (signals.length === 0) {
    listBody = (
      <EmptyState
        icon={<PulseRegular />}
        title="No Drift Signals"
        description="No signals match the current filters, or no governance drift has been detected for this tenant yet."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Governance drift signals">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'targetName' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                onClick={() => toggleSort('targetName')}
                style={{ width: '100%' }}
              >
                Target
              </TableHeaderCell>
              <TableHeaderCell>Category</TableHeaderCell>
              <TableHeaderCell>Signal Kind</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'score' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                onClick={() => toggleSort('score')}
              >
                Score
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'severity' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                onClick={() => toggleSort('severity')}
              >
                Severity
              </TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'detectedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                onClick={() => toggleSort('detectedAt')}
              >
                Detected
              </TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {signals.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <div>
                    <Text weight="semibold">{item.targetName}</Text>
                    <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                      {item.targetExternalId}
                    </Caption1>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline">{item.category}</Badge>
                </TableCell>
                <TableCell>
                  <Body2>{item.signalKind}</Body2>
                </TableCell>
                <TableCell>
                  <Body2>{item.score.toFixed(1)}</Body2>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={severityColor(item.severity)}>{item.severity}</Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={statusColor(item.status)}>{item.status}</Badge>
                </TableCell>
                <TableCell>
                  <Body2>{new Date(item.detectedAt).toLocaleString()}</Body2>
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<ChevronRightRegular />}
                    size="small"
                    aria-label="View signal details"
                    onClick={() => setSelectedSignal(item)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  const isTerminal = selectedSignal ? TERMINAL_STATUSES.includes(selectedSignal.status) : false;

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} position="top-end" />

      {/* Identity-state lookup — sibling above the list frame, non-scrolling. */}
      <IdentityStatePanel />

      <div className={styles.listSection}>
        <ListPageLayout
          header={
            <MasterDetailHeader
              crumbs={crumbs}
              busy={isFetching && !isLoading}
              subtitle="Detected drift signals and per-identity governance-drift state across the F030 graph"
              actions={
                <div className={styles.headerActions}>
                  <Button
                    appearance="secondary"
                    icon={recomputeMutation.isPending ? <Spinner size="tiny" /> : <ArrowClockwise24Regular />}
                    disabled={recomputeMutation.isPending}
                    onClick={() => recomputeMutation.mutate()}
                  >
                    Recompute
                  </Button>
                  <Button
                    appearance="primary"
                    icon={<Bookmark24Regular />}
                    onClick={() => setIsRebaselineDialogOpen(true)}
                  >
                    Rebaseline
                  </Button>
                </div>
              }
            />
          }
          toolbar={
            <ListToolbar
              search={state.search}
              onSearchChange={setSearch}
              searchPlaceholder="Search signals…"
              extraFilters={
                <>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="Category"
                    value={filterState.category}
                    onOptionSelect={(_, data) =>
                      updateFilter('category', (data.optionValue || '') as DriftCategory | '')
                    }
                  >
                    <Option value="">All Categories</Option>
                    {DRIFT_CATEGORY_OPTIONS.map((v) => (
                      <Option key={v} value={v}>
                        {MEASURED_DRIFT_CATEGORIES.includes(v) ? v : `${v} (not yet measured)`}
                      </Option>
                    ))}
                  </Dropdown>
                  <Dropdown
                    className={styles.filterDropdown}
                    placeholder="Status"
                    value={filterState.status}
                    onOptionSelect={(_, data) =>
                      updateFilter('status', (data.optionValue || '') as DriftStatus | '')
                    }
                  >
                    <Option value="">All Statuses</Option>
                    {DRIFT_STATUS_OPTIONS.map((v) => <Option key={v} value={v}>{v}</Option>)}
                  </Dropdown>
                </>
              }
              onRefresh={refetch}
              isRefreshing={isFetching}
              testIdPrefix="governance-drift"
            />
          }
          footer={
            <Pager
              page={state.page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="governance-drift-pager"
            />
          }
        >
          {listBody}
        </ListPageLayout>
      </div>

      {/* Signal detail — rendered straight from the already-loaded row, no second fetch. */}
      <DetailDrawer
        open={selectedSignal !== null}
        onClose={() => setSelectedSignal(null)}
        title="Drift Signal Details"
      >
        {selectedSignal && (
          <DetailSection title={selectedSignal.targetName}>
            <Text style={{ fontFamily: 'monospace' }}>{selectedSignal.targetExternalId}</Text>
            <DetailMetadataGrid>
              <DetailField label="Category">
                <Badge appearance="outline">{selectedSignal.category}</Badge>
              </DetailField>
              <DetailField label="Signal Kind">
                <Text>{selectedSignal.signalKind}</Text>
              </DetailField>
              <DetailField label="Score">
                <Text>{selectedSignal.score.toFixed(1)}</Text>
              </DetailField>
              <DetailField label="Severity">
                <Badge appearance="tint" color={severityColor(selectedSignal.severity)}>{selectedSignal.severity}</Badge>
              </DetailField>
              <DetailField label="Status">
                <Badge appearance="tint" color={statusColor(selectedSignal.status)}>{selectedSignal.status}</Badge>
              </DetailField>
              <DetailField label="Detected">
                <Text>{new Date(selectedSignal.detectedAt).toLocaleString()}</Text>
              </DetailField>
              {isTerminal && (
                <>
                  <DetailField label="Resolved">
                    <Text>{selectedSignal.resolvedAt ? new Date(selectedSignal.resolvedAt).toLocaleString() : '—'}</Text>
                  </DetailField>
                  <DetailField label="Resolution">
                    <Text>{selectedSignal.resolution ?? '—'}</Text>
                  </DetailField>
                </>
              )}
            </DetailMetadataGrid>
          </DetailSection>
        )}
      </DetailDrawer>

      {/* Rebaseline */}
      <Dialog
        open={isRebaselineDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) closeRebaselineDialog();
        }}
      >
        <DialogSurface>
          <DialogTitle>Cut New Baseline</DialogTitle>
          <DialogBody>
            <DialogContent>
              <Field label="Reason (optional)">
                <Textarea
                  value={rebaselineReason}
                  onChange={(_, data) => setRebaselineReason(data.value)}
                  placeholder="Why is this baseline being cut?"
                  resize="vertical"
                  disabled={rebaselineMutation.isPending}
                />
              </Field>
              <div className={styles.dialogActions}>
                <Button appearance="secondary" onClick={closeRebaselineDialog} disabled={rebaselineMutation.isPending}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleRebaselineSubmit}
                  disabled={rebaselineMutation.isPending}
                  icon={rebaselineMutation.isPending ? <Spinner size="tiny" /> : undefined}
                >
                  {rebaselineMutation.isPending ? 'Cutting…' : 'Cut Baseline'}
                </Button>
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
